import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Square } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { musicUrl } from "@/components/editor/stickers/MusicPanel";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const bars = (id, n = 64) => {
  let h = [...id].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  return Array.from({ length: n }, () => { h = (h * 1103515245 + 12345) >>> 0; return 0.25 + ((h >>> 8) % 1000) / 1333; });
};

export const PassagePicker = ({ id, dur, len, start, onChange }) => {
  const { t } = useI18n();
  const rail = useRef(null);
  const audio = useRef(null);
  const timer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const wave = useMemo(() => bars(id), [id]);
  const win = Math.min(1, len / dur);
  const maxStart = Math.max(0, dur - Math.min(len, dur));
  const s = Math.min(start, maxStart);
  const stop = () => { audio.current?.pause(); clearTimeout(timer.current); setPlaying(false); };
  useEffect(() => stop, [id]);
  const listen = () => {
    if (playing) return stop();
    audio.current?.pause();
    audio.current = new Audio(musicUrl(id));
    audio.current.currentTime = s;
    audio.current.play().catch(() => {});
    timer.current = setTimeout(stop, Math.min(len, 20) * 1000);
    setPlaying(true);
  };
  const down = (e) => {
    e.preventDefault();
    const r = rail.current.getBoundingClientRect();
    const grab = e.target.dataset.window ? e.clientX - (r.left + (s / dur) * r.width) : (win * r.width) / 2;
    const move = (ev) => onChange(+Math.min(maxStart, Math.max(0, ((ev.clientX - grab - r.left) / r.width) * dur)).toFixed(1));
    move(e);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <div className="space-y-1.5" data-testid="music-passage">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-slate-400">{t("mu.passage")}</span>
        <span className="font-mono text-white" data-testid="music-passage-value">{fmt(s)} → {fmt(Math.min(dur, s + len))}</span>
      </div>
      <div ref={rail} onPointerDown={down} className="relative h-12 rounded-md bg-black/50 border border-white/10 cursor-pointer touch-none select-none overflow-hidden" data-testid="music-passage-rail">
        <div className="absolute inset-0 flex items-center gap-[2px] px-1 pointer-events-none">
          {wave.map((v, i) => <span key={i} className="flex-1 rounded-full bg-[#3ad7ff]/35" style={{ height: `${v * 80}%` }} />)}
        </div>
        <div data-window="1" data-testid="music-passage-window" className="absolute inset-y-0 rounded-md border-2 border-[#a746ff] bg-[#a746ff]/20 shadow-[0_0_14px_rgba(167,70,255,0.45)] cursor-grab active:cursor-grabbing"
          style={{ left: `${(s / dur) * 100}%`, width: `${win * 100}%` }} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] text-slate-500" data-testid="music-passage-hint">{t(len >= dur ? "mu.passage_loop" : "mu.passage_hint")}</p>
        <button type="button" onClick={listen} data-testid="music-passage-listen" className="cx-metal rounded-full px-3 py-1 text-[11px] font-semibold flex items-center gap-1.5 shrink-0">
          {playing ? <Square className="w-3 h-3 fill-current" /> : <Play className="w-3 h-3 fill-current" />}{t(playing ? "mu.stop" : "mu.listen")}
        </button>
      </div>
    </div>
  );
};
